import React, { useEffect, useState } from "react";
import { getAllPaginatedActions } from "../../../src/API/Actions/actionService";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../src/components/ui/pagination";
import { Button } from "../../../src/components/ui/button";
import { CiSearch } from "react-icons/ci";
import { Input } from "../../../src/components/ui/input";
import WaterLoader from "../../components/Loader/WaterLoader";

interface NewsItem {
  title: string;
  date: string;
  description: string;
  image: string;
  linkUrl: string;
  slug: string;
}

interface NewsCardProps {
  title: string;
  date: string;
  description: string;
  image: string;
  linkUrl: string;
  slug: string;
}

const NewsCard: React.FC<NewsCardProps> = ({
  title,
  date,
  description,
  image,
  slug,
}) => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD || "";

  return (
    <div className="max-w-sm w-full md:w-80 m-4 overflow-hidden bg-white rounded-lg shadow-lg h-[300px] flex flex-col">
      <img
        className="object-cover w-full h-48"
        src={`${uploadUrl}/actions/${image}`}
        alt={title}
      />
      <div className="flex-grow px-6 py-4">
        <div className="mb-2 font-bold line-clamp-2">{title}</div>
        <div
          className="w-full text-sm line-clamp-3"
          dangerouslySetInnerHTML={{
            __html:
              description.substring(0, 80).replace(/<\/?strong>/g, "") +
              (description.length > 80 ? "..." : ""),
          }}
        />
      </div>
      <div className="flex items-center justify-center w-full px-4 py-4">
        <a
          href={`/initiatives/${slug}`}
          className="w-full text-sm text-primary hover:text-blue-700"
        >
          <Button className="w-full">Voir plus</Button>
        </a>
      </div>
    </div>
  );
};

const AllEngagment: React.FC = () => {
  const [actions, setActions] = useState<NewsItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getAllPaginatedActions(page, keyword);
      if (response) {
        setActions(response.actions || []);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      console.error("Failed to fetch actions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  const handleNextPageChange = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handlePreviousPageChange = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    setPage(1);  
  };

  const handleSearchClick = () => {
    setPage(1);  
    fetchData();
  };

  return (
    <div className="container p-8 mx-auto">
      <h1 className="mb-4 text-2xl font-bold text-center text-black">
        Nos Engagements pour un avenir durable
      </h1>
      <h2 className="text-center">
        Retrouvez ici l'ensemble de nos engagements pour l'environnement, la
        qualité de service et la transparence.
      </h2>
      <div className="relative mx-auto w-[600px] mt-20">
        <div className="relative flex items-center mt-8">
          <div className="absolute pointer-events-none left-3">
            <CiSearch className="w-5 h-5 text-black" />
          </div>
          <Input
            className="py-3 pl-12 pr-12 bg-white border border-primary text-primary h-9 rounded-3xl focus:outline-none focus:border-primary"
            placeholder="Rechercher une initiative..."
            value={keyword}
            onChange={handleInputChange}
          />
          <Button
            className="absolute top-0 bottom-0 right-0 px-6 py-3 text-white rounded-r-md bg-primary rounded-3xl"
            onClick={handleSearchClick}
          >
            Rechercher
          </Button>
        </div>
      </div>

      {loading ? (
        <WaterLoader />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 mt-8 md:grid-cols-3">
            {actions.map((item, index) => (
              <NewsCard
                key={index}
                title={item.title}
                date={item.date}
                description={item.description}
                image={item.image}
                linkUrl={item.linkUrl}
                slug={item.slug}
              />
            ))}
          </div>
          <div className="flex justify-center">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    className="cursor-pointer hover:bg-transparent"
                    onClick={handlePreviousPageChange}
                  />
                </PaginationItem>
                {[...Array(totalPages)].map((_, index) => (
                  <PaginationItem key={index}>
                    <PaginationLink
                      className="cursor-pointer hover:bg-transparent"
                      onClick={() => setPage(index + 1)}
                    >
                      <Button
                        variant="outline"
                        size="icon"
                        className="w-10 h-10 text-white rounded-full bg-primary"
                      >
                        {index + 1}
                      </Button>
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={handleNextPageChange}
                    className="cursor-pointer hover:bg-transparent text-primary"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </>
      )}
    </div>
  );
};

export default AllEngagment;
